/**
 * <pdp-lightbox-zoom>: the PDP lightbox opens on the whole image, and zooms on demand.
 *
 * Stands in for Horizon's <drag-zoom-wrapper> inside the zoom dialog. It is a new element
 * rather than a change to Horizon's for two reasons:
 *
 *   1. `assets/drag-zoom-wrapper.js` is gitignored here, so it cannot be changed through this
 *      repo. Its `DEFAULT_ZOOM = 1.5` is why the lightbox opened zoomed in on mobile.
 *   2. A fork would be an un-ignored copy of a Horizon file that drifts on every Horizon update
 *      and still imports Horizon's `./utilities.js`.
 *
 * So this owns zoom on both breakpoints, starting at scale 1. The gesture maths (pinch, drag,
 * double-tap, translation clamping) is ported from `drag-zoom-wrapper.js`.
 *
 * It does NOT touch navigation. Vertical scroll between images, the thumbnail strip and the
 * view-transition on close are all `zoom-dialog.js`, untouched.
 *
 * Contract with `zoom-dialog.js` that must not break:
 *   - `#loadHighResolutionImage` REPLACES the <img> node when an item becomes most visible, so
 *     the image is read lazily on every use and never cached.
 *   - `closeDialog()` calls `dialog.close()`, so the native `close` event is the reset signal.
 *     Horizon monkey-patches `zoomDialog.close` instead; we do not, because two elements doing
 *     that to the same dialog would wrap each other.
 *
 * Transform convention, inherited from Horizon so the snippet's mobile CSS keeps working:
 *   `transform: scale(S) translate(Tx, Ty)`
 * translate is applied INSIDE the scale, so a translate of Tx moves the image S*Tx screen px.
 * Every translate value here is therefore in pre-scale units, which is what the `/ scale`
 * divisions below are for.
 */

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/** Desktop click target: actual pixels, but never less than this, so a small source still zooms visibly. */
const DESKTOP_MIN_ZOOM = 2;

/** Mobile double-tap target, from the fitted image. Pinch goes further, up to MAX_ZOOM. */
const MOBILE_DOUBLE_TAP_ZOOM = 2;

/** Must match the transition duration in the snippet's {% stylesheet %}. */
const ZOOM_ANIMATION_MS = 200;

const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

const MOBILE_BREAKPOINT = "(max-width: 749px)";

/**
 * Clamp a number to a range.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Size of an `object-fit: contain` image inside its box.
 *
 * When the natural size is unknown (the image has not decoded yet, or it was just swapped for
 * the high-resolution one), fall back to the box itself. That is the no-letterbox case, which
 * yields the correct overflow for an image that fills the box and never over-constrains panning.
 *
 * @param {{naturalWidth: number, naturalHeight: number, boxWidth: number, boxHeight: number}} args
 * @returns {{width: number, height: number}}
 */
export function fitContain({
  naturalWidth,
  naturalHeight,
  boxWidth,
  boxHeight,
}) {
  if (
    !(naturalWidth > 0) ||
    !(naturalHeight > 0) ||
    !(boxWidth > 0) ||
    !(boxHeight > 0)
  ) {
    return {
      width: Math.max(0, boxWidth) || 0,
      height: Math.max(0, boxHeight) || 0,
    };
  }

  const imageRatio = naturalWidth / naturalHeight;
  const boxRatio = boxWidth / boxHeight;

  // Wider than the box: width fits exactly, letterboxed top and bottom. Otherwise the reverse.
  return imageRatio > boxRatio
    ? { width: boxWidth, height: boxWidth / imageRatio }
    : { width: boxHeight * imageRatio, height: boxHeight };
}

/**
 * How far the image may be panned from centre, in pre-scale units.
 *
 * The image starts centred, so it may move half the overflow in each direction. Dividing by the
 * scale converts screen px to the pre-scale units the transform expects.
 *
 * @param {{fittedWidth: number, fittedHeight: number, boxWidth: number, boxHeight: number, scale: number}} args
 * @returns {{x: number, y: number}}
 */
export function maxTranslate({
  fittedWidth,
  fittedHeight,
  boxWidth,
  boxHeight,
  scale,
}) {
  if (!(scale > MIN_ZOOM)) return { x: 0, y: 0 };

  const horizontalOverflow = Math.max(0, fittedWidth * scale - boxWidth);
  const verticalOverflow = Math.max(0, fittedHeight * scale - boxHeight);

  return {
    x: horizontalOverflow / 2 / scale,
    y: verticalOverflow / 2 / scale,
  };
}

/**
 * The scale at which one image pixel covers one CSS pixel, floored so a small source still
 * zooms visibly and capped at MAX_ZOOM.
 *
 * `fittedWidth` is the rendered width of the contain-fitted image, NOT the wrapper: a portrait
 * image in a landscape box is letterboxed, and measuring the wrapper would understate the scale.
 *
 * @param {{naturalWidth: number, fittedWidth: number}} args
 * @returns {number}
 */
export function actualPixelScale({ naturalWidth, fittedWidth }) {
  if (!(naturalWidth > 0) || !(fittedWidth > 0)) return DESKTOP_MIN_ZOOM;

  return clamp(
    Math.max(naturalWidth / fittedWidth, DESKTOP_MIN_ZOOM),
    MIN_ZOOM,
    MAX_ZOOM,
  );
}

/**
 * Translation that keeps the point under the cursor or fingers stationary while the scale
 * changes. Ported unchanged from Horizon's `#handleDoubleTapFromTouch` / `#processZoomGesture`.
 *
 * @param {{fromScale: number, toScale: number, translate: {x: number, y: number},
 *          point: {x: number, y: number}, centre: {x: number, y: number}}} args
 * @returns {{x: number, y: number}}
 */
export function zoomAtPoint({ fromScale, toScale, translate, point, centre }) {
  if (!(toScale > MIN_ZOOM)) return { x: 0, y: 0 };
  if (!(fromScale > 0) || !(toScale > 0))
    return { x: translate.x, y: translate.y };

  const scaleDelta = toScale / fromScale - 1;

  return {
    x: translate.x - ((point.x - centre.x) * scaleDelta) / toScale,
    y: translate.y - ((point.y - centre.y) * scaleDelta) / toScale,
  };
}

/**
 * Desktop pan: map the cursor across the box onto the image edge to edge, so the left edge of
 * the box shows the left edge of the image.
 *
 * At fraction 0 across the box we want the image pushed right by its full limit to reveal its
 * left edge, and at 1 the opposite: hence `limit * (1 - 2f)`.
 *
 * @param {{point: {x: number, y: number}, boxWidth: number, boxHeight: number,
 *          limits: {x: number, y: number}}} args
 * @returns {{x: number, y: number}}
 */
export function panFromPointer({ point, boxWidth, boxHeight, limits }) {
  const fractionX = boxWidth > 0 ? clamp(point.x / boxWidth, 0, 1) : 0.5;
  const fractionY = boxHeight > 0 ? clamp(point.y / boxHeight, 0, 1) : 0.5;

  return {
    x: limits.x * (1 - 2 * fractionX),
    y: limits.y * (1 - 2 * fractionY),
  };
}

/**
 * Distance between two points, accepting either {x, y} or a Touch.
 * @param {{x?: number, y?: number, clientX?: number, clientY?: number}} from
 * @param {{x?: number, y?: number, clientX?: number, clientY?: number}} to
 * @returns {number}
 */
export function getDistance(from, to) {
  const dx = (from.x ?? from.clientX ?? 0) - (to.x ?? to.clientX ?? 0);
  const dy = (from.y ?? from.clientY ?? 0) - (to.y ?? to.clientY ?? 0);
  return Math.hypot(dx, dy);
}

let watchingInputModality = false;

/**
 * Mark the lightbox dialogs with how the shopper last interacted: `data-keyboard` after a key,
 * removed after a pointer or touch. The snippet's stylesheet uses it for the close button's
 * focus ring. `showModal()` focuses that button on open, and the browser then matches
 * `:focus-visible` after a click or tap too, so the pseudo-class alone cannot keep the ring
 * off for a pointer and on for a keyboard. Capture phase, so it is set before a keyboard
 * Enter on the gallery opens the dialog. Installed once for the page.
 */
function watchInputModality() {
  if (watchingInputModality || typeof document === "undefined") return;
  watchingInputModality = true;

  /** @param {boolean} keyboard */
  const mark = (keyboard) => {
    for (const dialog of document.querySelectorAll("zoom-dialog dialog")) {
      dialog.toggleAttribute("data-keyboard", keyboard);
    }
  };
  document.addEventListener("keydown", () => mark(true), true);
  document.addEventListener("pointerdown", () => mark(false), true);
}

/**
 * `HTMLElement` does not exist outside a browser, and `class X extends undefined` throws at
 * definition time, which would stop the pure helpers above from being imported by unit tests.
 * Extending a plain class there keeps the module importable; nothing instantiates it.
 */
const ElementBase =
  typeof HTMLElement === "undefined"
    ? /** @type {any} */ (class {})
    : HTMLElement;

export class PdpLightboxZoom extends ElementBase {
  #controller = new AbortController();
  #scale = MIN_ZOOM;
  #translate = { x: 0, y: 0 };
  #frame = /** @type {number | null} */ (null);

  #startScale = MIN_ZOOM;
  #startDistance = 0;
  #startPoint = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #isDragging = false;
  #draggedBeyondThreshold = false;
  #lastTapTime = 0;
  #lastTapPoint = /** @type {{x: number, y: number} | null} */ (null);

  #animationTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
  #resizeObserver = /** @type {ResizeObserver | null} */ (null);
  #intersectionObserver = /** @type {IntersectionObserver | null} */ (null);

  /**
   * Read lazily every time: `zoom-dialog.js` replaces this node with the high-resolution image
   * once the item becomes most visible, so a cached reference goes stale and silently detaches
   * the zoom from what is on screen.
   * @returns {HTMLImageElement | null}
   */
  get image() {
    return this.querySelector("img");
  }

  get #isMobile() {
    return (
      typeof matchMedia === "function" && matchMedia(MOBILE_BREAKPOINT).matches
    );
  }

  connectedCallback() {
    watchInputModality();

    const { signal } = this.#controller;
    const passive = { signal };
    const active = { passive: false, signal };

    // Both breakpoints are wired up once and the handlers check the media query, rather than
    // binding on resize. Horizon binds touch listeners only if the first layout was mobile and
    // re-checks on resize; a dialog opened on desktop and then narrowed ends up half-wired.
    // Bound at the call site rather than declared as arrow-function properties: eslint's
    // sort-class-members requires every class property to precede every getter and method,
    // which would move all eight handlers away from the code they belong to. Removal still
    // works through the AbortController signal, so a stable reference is not needed.
    this.addEventListener(
      "click",
      (event) => this.#handleClick(event),
      passive,
    );
    this.addEventListener(
      "pointermove",
      (event) => this.#handlePointerMove(event),
      passive,
    );
    this.addEventListener(
      "touchstart",
      (event) => this.#handleTouchStart(event),
      active,
    );
    this.addEventListener(
      "touchmove",
      (event) => this.#handleTouchMove(event),
      active,
    );
    this.addEventListener(
      "touchend",
      (event) => this.#handleTouchEnd(event),
      passive,
    );

    // `closeDialog()` calls `dialog.close()`, which fires this natively.
    this.closest("dialog")?.addEventListener(
      "close",
      () => this.#reset(),
      passive,
    );

    if (typeof ResizeObserver === "function") {
      this.#resizeObserver = new ResizeObserver(() => this.#requestTransform());
      this.#resizeObserver.observe(this);
    }

    // Reset when the item scrolls out of view, so the next image is always reached fitted.
    if (typeof IntersectionObserver === "function") {
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) if (!entry.isIntersecting) this.#reset();
        },
        { threshold: 0 },
      );
      this.#intersectionObserver.observe(this);
    }

    this.#applyTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#animationTimer != null) clearTimeout(this.#animationTimer);
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#cancelFrame();
  }

  /** Box the image is fitted into. */
  get #box() {
    const rect = this.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }

  get #fitted() {
    const image = this.image;
    const box = this.#box;

    return fitContain({
      naturalWidth: image?.naturalWidth ?? 0,
      naturalHeight: image?.naturalHeight ?? 0,
      boxWidth: box.width,
      boxHeight: box.height,
    });
  }

  get #limits() {
    const box = this.#box;
    const fitted = this.#fitted;

    return maxTranslate({
      fittedWidth: fitted.width,
      fittedHeight: fitted.height,
      boxWidth: box.width,
      boxHeight: box.height,
      scale: this.#scale,
    });
  }

  /**
   * Position of an event relative to this element.
   * @param {{clientX: number, clientY: number}} source
   */
  #localPoint(source) {
    const rect = this.getBoundingClientRect();
    return { x: source.clientX - rect.left, y: source.clientY - rect.top };
  }

  get #centre() {
    const box = this.#box;
    return { x: box.width / 2, y: box.height / 2 };
  }

  // Desktop ---------------------------------------------------------------------------------

  /** @param {MouseEvent} event */
  #handleClick(event) {
    if (this.#isMobile) return;

    if (this.#scale > MIN_ZOOM) {
      this.#setZoom(MIN_ZOOM, { x: 0, y: 0 });
      return;
    }

    const toScale = actualPixelScale({
      naturalWidth: this.image?.naturalWidth ?? 0,
      fittedWidth: this.#fitted.width,
    });

    const box = this.#box;

    // The same mapping the cursor pans with, deliberately, so the first mouse move after the
    // click continues it rather than jumping. For an image that fills the box the two are
    // algebraically identical; they only diverge once it is letterboxed, where a jump would be
    // obvious.
    this.#setZoom(
      toScale,
      panFromPointer({
        point: this.#localPoint(event),
        boxWidth: box.width,
        boxHeight: box.height,
        limits: maxTranslate({
          fittedWidth: this.#fitted.width,
          fittedHeight: this.#fitted.height,
          boxWidth: box.width,
          boxHeight: box.height,
          scale: toScale,
        }),
      }),
    );
  }

  /** @param {PointerEvent} event */
  #handlePointerMove(event) {
    if (
      this.#isMobile ||
      this.#scale <= MIN_ZOOM ||
      event.pointerType === "touch"
    )
      return;

    const box = this.#box;

    this.#translate = panFromPointer({
      point: this.#localPoint(event),
      boxWidth: box.width,
      boxHeight: box.height,
      limits: this.#limits,
    });

    this.#requestTransform();
  }

  // Mobile ----------------------------------------------------------------------------------

  /** @param {TouchEvent} event */
  #handleTouchStart(event) {
    if (!this.#isMobile) return;

    const [touch1, touch2] = event.touches;

    if (event.touches.length === 2 && touch1 && touch2) {
      this.#startDistance = getDistance(touch1, touch2);
      this.#startScale = this.#scale;
      this.#isDragging = false;
      return;
    }

    if (event.touches.length !== 1 || !touch1) return;

    const now = performance.now();
    const isDoubleTap =
      now - this.#lastTapTime < DOUBLE_TAP_DELAY &&
      this.#lastTapPoint != null &&
      getDistance(touch1, this.#lastTapPoint) < DOUBLE_TAP_DISTANCE;

    if (isDoubleTap) {
      // Only now: at scale 1 a single tap must stay a tap, so the browser keeps handling the
      // swipe that moves to the next image.
      event.preventDefault();
      this.#handleDoubleTap(touch1);
      this.#lastTapTime = 0;
      this.#lastTapPoint = null;
      return;
    }

    this.#lastTapTime = now;
    this.#lastTapPoint = { x: touch1.clientX, y: touch1.clientY };
    this.#startPoint = { x: touch1.clientX, y: touch1.clientY };
    this.#startTranslate = { ...this.#translate };
    this.#isDragging = true;
    this.#draggedBeyondThreshold = false;
  }

  /** @param {Touch} touch */
  #handleDoubleTap(touch) {
    if (this.#scale > MIN_ZOOM) {
      this.#setZoom(MIN_ZOOM, { x: 0, y: 0 });
      return;
    }

    const toScale = MOBILE_DOUBLE_TAP_ZOOM;

    this.#setZoom(
      toScale,
      zoomAtPoint({
        fromScale: this.#scale,
        toScale,
        translate: this.#translate,
        point: this.#localPoint(touch),
        centre: this.#centre,
      }),
    );
  }

  /** @param {TouchEvent} event */
  #handleTouchMove(event) {
    if (!this.#isMobile) return;

    const [touch1, touch2] = event.touches;

    if (event.touches.length === 2 && touch1 && touch2) {
      event.preventDefault();
      this.#pinch(touch1, touch2);
      return;
    }

    if (event.touches.length !== 1 || !touch1 || !this.#isDragging) return;

    // Recorded at every scale, before the early return below: a touch that travels is a swipe
    // or a pan, not a tap, so it must not pair with the next touch as a double-tap. Two quick
    // swipes at scale 1 would otherwise zoom instead of moving to the next image.
    if (
      !this.#draggedBeyondThreshold &&
      getDistance(touch1, this.#startPoint) >= DRAG_THRESHOLD
    ) {
      this.#draggedBeyondThreshold = true;
      this.#lastTapTime = 0;
      this.#lastTapPoint = null;
    }

    // The one rule that keeps swipe-to-next-image alive: only swallow the gesture once there is
    // something to pan. Horizon prevents default unconditionally, which only works because it
    // starts above scale 1.
    if (this.#scale <= MIN_ZOOM) return;

    event.preventDefault();

    if (!this.#draggedBeyondThreshold) return;

    this.#translate = {
      x:
        this.#startTranslate.x +
        (touch1.clientX - this.#startPoint.x) / this.#scale,
      y:
        this.#startTranslate.y +
        (touch1.clientY - this.#startPoint.y) / this.#scale,
    };

    this.#requestTransform();
  }

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #pinch(touch1, touch2) {
    if (!(this.#startDistance > 0)) return;

    const fromScale = this.#scale;
    const toScale = clamp(
      (getDistance(touch1, touch2) / this.#startDistance) * this.#startScale,
      MIN_ZOOM,
      MAX_ZOOM,
    );

    const midpoint = this.#localPoint({
      clientX: (touch1.clientX + touch2.clientX) / 2,
      clientY: (touch1.clientY + touch2.clientY) / 2,
    });

    this.#scale = toScale;
    this.#translate = zoomAtPoint({
      fromScale,
      toScale,
      translate: this.#translate,
      point: midpoint,
      centre: this.#centre,
    });

    this.#isDragging = false;
    this.#requestTransform();
  }

  /** @param {TouchEvent} event */
  #handleTouchEnd(event) {
    if (event.touches.length > 0) return;

    this.#isDragging = false;
    this.#draggedBeyondThreshold = false;
    this.#requestTransform();
  }

  // State -----------------------------------------------------------------------------------

  /**
   * @param {number} scale
   * @param {{x: number, y: number}} translate
   */
  #setZoom(scale, translate) {
    this.#scale = clamp(scale, MIN_ZOOM, MAX_ZOOM);
    this.#translate = { ...translate };

    // Only a discrete zoom change animates. Panning writes a new transform on every pointermove,
    // and a transition on those would leave the image trailing the cursor by the whole duration.
    this.#animate();
    this.#requestTransform();
  }

  #animate() {
    this.toggleAttribute("data-animating", true);
    if (this.#animationTimer != null) clearTimeout(this.#animationTimer);
    this.#animationTimer = setTimeout(() => {
      this.#animationTimer = null;
      this.toggleAttribute("data-animating", false);
    }, ZOOM_ANIMATION_MS);
  }

  #reset() {
    // Silent: the item is off screen or the dialog has closed, so there is nothing to animate.
    if (this.#animationTimer != null) clearTimeout(this.#animationTimer);
    this.#animationTimer = null;
    this.toggleAttribute("data-animating", false);

    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#startPoint = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#startDistance = 0;
    this.#isDragging = false;
    this.#draggedBeyondThreshold = false;
    this.#lastTapTime = 0;
    this.#lastTapPoint = null;
    this.#applyTransform();
  }

  #requestTransform() {
    if (this.#frame != null) return;

    if (typeof requestAnimationFrame !== "function") {
      this.#applyTransform();
      return;
    }

    this.#frame = requestAnimationFrame(() => this.#applyTransform());
  }

  #cancelFrame() {
    if (this.#frame == null) return;
    if (typeof cancelAnimationFrame === "function")
      cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #applyTransform() {
    this.#frame = null;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    const limits = this.#limits;
    this.#translate = {
      x: clamp(this.#translate.x, -limits.x, limits.x),
      y: clamp(this.#translate.y, -limits.y, limits.y),
    };

    this.style.setProperty("--pdp-zoom-scale", String(this.#scale));
    this.style.setProperty("--pdp-zoom-x", `${this.#translate.x}px`);
    this.style.setProperty("--pdp-zoom-y", `${this.#translate.y}px`);
    this.toggleAttribute("data-zoomed", this.#scale > MIN_ZOOM);
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("pdp-lightbox-zoom")
) {
  customElements.define("pdp-lightbox-zoom", PdpLightboxZoom);
}
